// =============================================================================
// API Client - Centralized HTTP client with global error handling
// =============================================================================
// v3.3: Production launch - Feb 2026
// v3.2: Added global 401 handler for session expiration
// =============================================================================

import type { ErrorResponse } from '@transakt/schemas';
import { useAuthStore } from '@/stores/auth';

/**
 * API base URL
 * Uses VITE_API_URL environment variable in production/staging,
 * falls back to relative path for local development (Vite proxy)
 */
const API_BASE = ((window as any).ENV?.API_URL || import.meta.env.VITE_API_URL)
  ? `${(window as any).ENV?.API_URL || import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

/**
 * Endpoints that should NOT trigger auto-logout on 401
 * (e.g., auth endpoints that naturally return 401 for unauthenticated users)
 */
const AUTH_ENDPOINTS = [
  '/auth/me',
  '/auth/logout',
  '/auth/send-code',
  '/auth/verify',
  '/auth/signup',
  '/auth/check-user',
  '/auth/share-signup',
  // Password auth v1 (2026-09): these return 401 for bad credentials — a
  // wrong password must show inline, never hard-redirect to /login.
  '/auth/login-password',
  '/auth/verify-mfa',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/change-password',
  '/auth/mfa',
];

/**
 * Endpoints for guest users - should not redirect to login on 401
 * (guest users should be redirected back to Gatekeeper, not agent login)
 */
const GUEST_ENDPOINTS = ['/public/guest/', '/public/chat/'];

/**
 * Check if an endpoint is an auth endpoint
 */
function isAuthEndpoint(endpoint: string): boolean {
  return AUTH_ENDPOINTS.some(authEndpoint => endpoint.startsWith(authEndpoint));
}

/**
 * Check if an endpoint is a guest endpoint (public buyer routes)
 */
function isGuestEndpoint(endpoint: string): boolean {
  return GUEST_ENDPOINTS.some(guestEndpoint => endpoint.includes(guestEndpoint));
}

/**
 * Handle 403 TRIAL_EXPIRED - force UI to re-evaluate trial status
 * Clears the dismissed modal state and forces Zustand re-render so usePlanGate()
 * re-evaluates `new Date() > trialEnd`, disabling buttons and showing the modal.
 */
function handleTrialExpired(): void {
  // Re-show the TrialExpiredModal
  localStorage.removeItem('transakt-trial-expired-dismissed');

  // Force Zustand re-render by setting organization to a new reference
  const state = useAuthStore.getState();
  if (state.organization) {
    useAuthStore.setState({ organization: { ...state.organization } });
  }
}

/**
 * Handle 401 unauthorized response - clear auth state and redirect to login
 */
function handleUnauthorized(endpoint: string): void {
  // Don't redirect if already on an auth endpoint (prevents redirect loops)
  if (isAuthEndpoint(endpoint)) {
    return;
  }

  // Don't redirect guest users to login - they should go back to Gatekeeper
  // The SharedPropertyPage will handle this by showing the Gatekeeper component
  if (isGuestEndpoint(endpoint)) {
    return;
  }

  // Clear the auth store (Zustand stores can be accessed outside React)
  const authStore = useAuthStore.getState();
  authStore.logout();

  // Redirect to login page
  // Using window.location.href ensures a full page reload which clears any stale state
  window.location.href = '/login';
}

/**
 * API Error class
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if this is an authentication error (401)
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if this is a not found error (404)
   */
  isNotFound(): boolean {
    return this.status === 404;
  }
}

/**
 * Make an API request
 * Includes global 401 handling for session expiration
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle no content responses
  if (response.status === 204) {
    return undefined as T;
  }

  // Handle 401 Unauthorized - session expired
  // Trigger logout and redirect BEFORE parsing response body
  if (response.status === 401) {
    handleUnauthorized(endpoint);

    // Still throw an error so the calling code knows something went wrong
    // (even though we're redirecting, the error provides context for logging)
    throw new ApiError(
      'UNAUTHORIZED',
      'Your session has expired. Please log in again.',
      401
    );
  }

  // Parse JSON response with error handling for empty/malformed bodies
  let data: unknown;
  try {
    const text = await response.text();
    // Only parse if there's actual content
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    // JSON parsing failed - could be empty body or malformed JSON
    if (!response.ok) {
      throw new ApiError(
        'PARSE_ERROR',
        `Server returned status ${response.status} with invalid response body`,
        response.status
      );
    }
    // For successful responses with parse errors, return empty object
    data = {};
  }

  // Handle 403 TRIAL_EXPIRED - force UI to show upgrade prompt
  if (response.status === 403) {
    const errorData = data as ErrorResponse;
    if (errorData.error?.code === 'TRIAL_EXPIRED') {
      handleTrialExpired();
      throw new ApiError(
        errorData.error.code,
        errorData.error.message || 'Your trial has expired. Please upgrade to continue.',
        response.status,
        errorData.error.details
      );
    }
  }

  // Handle other error responses
  if (!response.ok) {
    const errorData = data as ErrorResponse;
    throw new ApiError(
      errorData.error?.code || 'UNKNOWN_ERROR',
      errorData.error?.message || 'An unknown error occurred',
      response.status,
      errorData.error?.details
    );
  }

  return data as T;
}

/**
 * API client with typed methods
 */
export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * Download a binary response (e.g., XLSX or PDF) from an authenticated endpoint.
   * Bypasses the JSON parsing in `request()`. Includes session cookie.
   * Throws ApiError on non-2xx (after attempting to read a JSON error body).
   */
  download: async (endpoint: string): Promise<Blob> => {
    const url = `${API_BASE}${endpoint}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      // Try to surface a server error message; fall back to a generic one.
      let message = `Download failed (${response.status})`;
      try {
        const errBody = await response.json();
        if (errBody?.error?.message) message = errBody.error.message;
        else if (errBody?.error) message = String(errBody.error);
      } catch {
        /* binary or empty body — keep the generic message */
      }
      throw new ApiError('DOWNLOAD_FAILED', message, response.status);
    }
    return response.blob();
  },
};
